<template>
	<div class="resource-planning-controls">
		<FilterSingleSelect
			v-if="hasTentativeBookingsFeature"
			v-model="bookingTypeFilterValue"
			:options="bookingTypeFilterOptions"
			:helper-label="$t('resource-planning__booking-type')"
			@update:model-value="onUpdateBookingTypeFilter"
		/>

		<FilterSingleSelect
			v-model="heatMapValue"
			:options="heatMapOptions"
			:helper-label="$t('resource-heatmap-title')"
			@update:model-value="onUpdateHeatMapOption"
		/>

		<ResourcePlanningControlsZoomSelect
			:zoom-level="zoomLevel"
			:show-zoom-level-select="showZoomLevelSelect"
			@zoom-in="$emit('zoom-in')"
			@zoom-out="$emit('zoom-out')"
			@zoom-to="$emit('zoom-to', $event)"
		/>

		<SuiButton
			v-if="showZoomToFit"
			v-tooltip="$t('fit_to_view')"
			class="resource-planning-controls__button"
			:aria-label="$t('fit_to_view')"
			@click="$emit('zoom-to-fit')"
		>
			<SuiIcon
				name="icon-zoomfit-16"
				class="resource-planning-controls__icon"
			/>
		</SuiButton>
	</div>
</template>

<script setup lang="ts">
import { usePermissions, useTranslation } from '@/composables';
import {
	BOOKING_TYPE_FILTER,
	HEAT_MAP_OPTIONS,
	SETTINGS,
} from '../../constants';
import ResourcePlanningControlsZoomSelect from './ResourcePlanningControlsZoomSelect.vue';
import { SuiButton, SuiIcon } from '@developers/scoro-ui';
import { type BookingTypeFilter, type HeatMapOption } from '../../types';
import { computed, ref } from 'vue';
import { saveResourcePlanningSetting } from '../ResourcePlanningGantt/ResourcePlanningGanttUtils';
import { Constants } from '@/constants';
import { FilterSingleSelect } from '@/features/Filter';
import { type Project } from '@/generated/graphql';

export type ResourcePlanningControlsProps = {
	projectId: Project['id'];
	heatMapValue: HeatMapOption;
	filterValue: BookingTypeFilter;
	zoomLevel: number;
	showZoomLevelSelect: boolean;
	showZoomToFit?: boolean;
};

const props = defineProps<ResourcePlanningControlsProps>();

const emit = defineEmits<{
	(e: 'zoom-in'): void;
	(e: 'zoom-out'): void;
	(e: 'zoom-to-fit'): void;
	(e: 'zoom-to', id: string): void;
	(e: 'update:heat-map-value', id: HeatMapOption): void;
	(e: 'update:filter-value', id: BookingTypeFilter): void;
}>();

const { t } = useTranslation();

const { userHas } = usePermissions();
const hasTentativeBookingsFeature = computed(() =>
	userHas(Constants.Feature.TENTATIVE_BOOKINGS)
);
const bookingTypeFilterValue = ref(props.filterValue);
const heatMapValue = ref(props.heatMapValue);

const bookingTypeFilterOptions = computed(() => [
	{
		id: BOOKING_TYPE_FILTER.FIXED_AND_TENTATIVE,
		title: t('resource-booking-type__fixed-and-tentative'),
	},
	{
		id: BOOKING_TYPE_FILTER.FIXED,
		title: t('resource-booking-type__fixed'),
	},
	{
		id: BOOKING_TYPE_FILTER.TENTATIVE,
		title: t('resource-booking-type__tentative'),
	},
]);

const onUpdateBookingTypeFilter = (filter: string) => {
	saveResourcePlanningSetting(props.projectId, SETTINGS.BOOKING_TYPE, filter);

	emit('update:filter-value', filter as BookingTypeFilter);
};

const heatMapOptions = computed(() => [
	{
		id: HEAT_MAP_OPTIONS.PERCENT,
		title: t('resource-heatmap-option__percent'),
	},
	{
		id: HEAT_MAP_OPTIONS.BOOKED,
		title: t('resource-heatmap-option__booked'),
	},
	{
		id: HEAT_MAP_OPTIONS.REMAINING,
		title: t('resource-heatmap-option__remaining'),
	},
]);

const onUpdateHeatMapOption = (option: string) => {
	emit('update:heat-map-value', option as HeatMapOption);
};
</script>

<style lang="scss">
@import '../ResourcePlanning/resourcePlanning';

.resource-planning-controls {
	display: flex;
	gap: 1rem;

	&__button {
		@extend %resource-planning-icon-button;
	}

	&__settings {
		display: flex;
		flex-direction: column;
		gap: 2rem;
		padding: 3rem;

		--sui-toggle-font-weight: #{$font-weight-bold};
	}
}
</style>
